import { useEffect, useRef, useState } from "react";
import {
  assistAvailable, askApplicationHelper, assistErrorMessage,
  type AssistTurn,
} from "../api/onboarding";

// Floating help bubble for the public /apply form. A prospect can ask what a
// field means while filling it out. Read-only: it only answers questions
// (the endpoint has no tools and cannot submit or change anything).

const GREETING =
  "Hi! I can help you fill out this application. Ask me anything — like what to " +
  "put for your use case, whether you need an EIN, or what counts as consent.";

export function OnboardingAssistant() {
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<AssistTurn[]>([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => { assistAvailable().then(setAvailable); }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, open, busy]);

  if (!available) return null;

  async function send() {
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    const history = msgs;
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      // Send prior turns as context (exclude the greeting so it isn't echoed).
      const ctx = history.filter((m, i) => !(i === 0 && m.role === "assistant"));
      const answer = await askApplicationHelper(q, ctx);
      setMsgs((m) => [...m, { role: "assistant", content: answer }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", content: assistErrorMessage(err) }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end">
      {open && (
        <div className="mb-3 w-[22rem] max-w-[calc(100vw-2.5rem)] h-[28rem] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-brand text-white">
            <div>
              <div className="font-semibold text-sm">Application help</div>
              <div className="text-[11px] text-white/80">Ask about any field</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close help"
                    className="text-white/80 hover:text-white text-lg leading-none">×</button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-brand text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"
                }`}>{m.content}</div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 text-slate-400 rounded-2xl rounded-bl-sm px-3 py-2 text-sm">
                  Thinking…
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 p-2 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask a question…"
              rows={1}
              maxLength={2000}
              className="flex-1 resize-none border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 max-h-24"
            />
            <button onClick={send} disabled={busy || !input.trim()}
                    className="bg-brand hover:bg-brand-dark disabled:opacity-50 text-white rounded-lg px-3 py-2 text-sm font-medium shrink-0">
              Send
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close help" : "Open help"}
        className="bg-brand hover:bg-brand-dark text-white rounded-full shadow-lg h-14 w-14 flex items-center justify-center transition"
      >
        {open ? (
          <span className="text-2xl leading-none">×</span>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
               className="h-6 w-6">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>
    </div>
  );
}
